import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import Swal from "sweetalert2";
import confetti from "canvas-confetti";
import { selectAdminToken } from "../../admin/sessionSlice";
import { updateGameSettings } from "../../functions/updateGameSettings";
import { setBackButtonUrl } from "../uiSlice";
import logoImg from "../images/desktop/logo.png";
import "./ThankYouSimple.css";

const S = {
  page: {
    minHeight: "90vh",
    padding: "40px 18px 70px",
    fontFamily: "'Nunito', sans-serif",
  },
  inner: { maxWidth: 560, margin: "0 auto" },
  headerRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 14,
    position: "relative",
    zIndex: 2,
  },
  backBtn: {
    background: "rgba(255,255,255,0.65)",
    border: "1px solid rgba(255,255,255,0.9)",
    borderRadius: 12,
    padding: "10px 14px",
    fontWeight: 900,
    color: "#1a2b5e",
    cursor: "pointer",
  },
  card: {
    marginTop: 10,
    background: "rgba(255,255,255,0.78)",
    border: "1.5px solid rgba(255,255,255,0.92)",
    borderRadius: 26,
    padding: "30px 24px",
    boxShadow: "0 10px 34px rgba(100,120,200,0.14)",
    textAlign: "center",
  },
  iconCircle: {
    width: 82,
    height: 82,
    borderRadius: "50%",
    margin: "0 auto 14px",
    background: "rgba(37,99,235,0.10)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  title: {
    fontSize: 42,
    fontWeight: 900,
    color: "#2563eb",
    marginBottom: 18,
  },
  scoreBox: {
    background: "white",
    borderRadius: 18,
    padding: "22px 16px",
    boxShadow: "0 10px 24px rgba(100,120,200,0.14)",
    margin: "0 auto 16px",
  },
  score: { fontSize: 76, fontWeight: 900, color: "#2563eb", lineHeight: 1 },
  scoreLabel: { fontSize: 16, fontWeight: 800, color: "#6b7db3", marginTop: 8 },
  pill: {
    display: "inline-flex",
    alignItems: "center",
    gap: 10,
    padding: "10px 16px",
    borderRadius: 999,
    background: "rgba(37,99,235,0.10)",
    color: "#1a2b5e",
    fontWeight: 900,
    marginBottom: 10,
  },
  check: {
    width: 18,
    height: 18,
    borderRadius: "50%",
    background: "#22c55e",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    color: "white",
    fontSize: 12,
  },
  meta: { marginTop: 10, color: "#6b7db3", fontWeight: 800, fontSize: 13 },
  btn: {
    width: "100%",
    marginTop: 18,
    padding: "16px 0",
    borderRadius: 999,
    border: "none",
    background: "linear-gradient(135deg,#4f9ef8,#2563eb)",
    color: "white",
    fontWeight: 900,
    fontSize: 18,
    cursor: "pointer",
    boxShadow: "0 10px 30px rgba(37,99,235,0.35)",
  },
};

function formatTimeMaybe(raw) {
  if (raw == null) return "";
  const s = String(raw).trim();
  if (s.includes(":")) {
    // If backend returns HH:MM:SS, display MM:SS
    const parts = s.split(":").filter(Boolean);
    if (parts.length >= 3) return `${parts[1].padStart(2, "0")}:${parts[2].padStart(2, "0")}`;
    if (parts.length === 2) return `${parts[0].padStart(2, "0")}:${parts[1].padStart(2, "0")}`;
    return s;
  }
  const n = Number(s);
  if (!Number.isFinite(n)) return s;
  const sec = Math.max(0, Math.floor(n));
  const mm = Math.floor(sec / 60);
  const ss = sec % 60;
  return `${String(mm).padStart(2, "0")}:${String(ss).padStart(2, "0")}`;
}

export default function ThankYou() {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const adminToken = useSelector(selectAdminToken);
  const { user } = useSelector((state) => state.auth);

  const storedUserData = useMemo(() => {
    try {
      const raw = sessionStorage.getItem("userData") || "{}";
      return JSON.parse(raw) || {};
    } catch {
      return {};
    }
  }, []);

  const tokenForApi = useMemo(() => {
    const t = adminToken != null ? String(adminToken).trim() : "";
    if (t.includes("&")) return t;
    const sid = storedUserData.sessionId;
    const oid = storedUserData.organizationId;
    return sid && oid ? `${sid}&${oid}` : "";
  }, [adminToken, storedUserData]);

  const state = location.state || {};
  const lobbyName = useMemo(() => {
    if (state?.lobby_name) return String(state.lobby_name);
    try {
      const cached = JSON.parse(sessionStorage.getItem("gameSettings") || "{}");
      if (cached?.lobby_name) return String(cached.lobby_name);
    } catch {
      /* ignore */
    }
    return "";
  }, [state?.lobby_name]);

  useEffect(() => {
    dispatch(setBackButtonUrl("/thankyou"));
  }, [dispatch]);

  const isAnchorMode = useMemo(() => {
    if (state?.isAnchorMode === true) return true;
    try {
      const cached = JSON.parse(sessionStorage.getItem("gameSettings") || "{}");
      return cached?.isAnchorMode === true;
    } catch {
      return false;
    }
  }, [state?.isAnchorMode]);

  const uid = user?.userId ?? user?.userid ?? user?.id ?? storedUserData.userId ?? storedUserData.userid ?? storedUserData.id;

  const [score, setScore] = useState(() => Number(state?.points) || 0);
  const [correct, setCorrect] = useState(() => Number(state?.correct) || 0);
  const [total, setTotal] = useState(() => Number(state?.total) || 0);
  const [time, setTime] = useState("");

  const stageIdFromCache = useMemo(() => {
    try {
      const cached = JSON.parse(sessionStorage.getItem("gameSettings") || "{}");
      return cached?.stageId != null && String(cached.stageId).trim() !== ""
        ? String(cached.stageId).trim()
        : null;
    } catch {
      return null;
    }
  }, []);

  useEffect(() => {
    document.documentElement.classList.add("thankyou-simple-bg");
    document.body.classList.add("thankyou-simple-bg");
    return () => {
      document.documentElement.classList.remove("thankyou-simple-bg");
      document.body.classList.remove("thankyou-simple-bg");
    };
  }, []);

  useEffect(() => {
    // Confetti burst once.
    try {
      confetti({ particleCount: 90, spread: 70, origin: { y: 0.45 } });
      window.setTimeout(() => confetti({ particleCount: 70, spread: 90, origin: { y: 0.35 } }), 350);
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    if (isAnchorMode) return;
    if (!tokenForApi || !uid || !lobbyName) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await updateGameSettings({
          token: tokenForApi,
          userId: uid,
          themeName: storedUserData.themeName || user?.themeName || "guess_me",
          ...(stageIdFromCache ? { stageId: stageIdFromCache } : {}),
          ...(lobbyName ? { lobby_name: lobbyName } : {}),
          show_question: true,
        });
        if (cancelled) return;
        if (data?.total_score != null) setScore(Number(data.total_score) || 0);
        else if (state?.points != null) setScore(Number(state.points) || 0);

        const c = Number(data?.correct);
        const t = Number(data?.total);
        if (state?.correct != null) setCorrect(Number(state.correct) || 0);
        else if (Number.isFinite(c)) setCorrect(c);

        if (state?.total != null) setTotal(Number(state.total) || 0);
        else if (Number.isFinite(t) && t > 0) setTotal(t);

        setTime(formatTimeMaybe(data?.time ?? state?.time ?? ""));
      } catch (e) {
        if (!cancelled) await Swal.fire("Error", e?.message || "Failed to load results", "error");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isAnchorMode, tokenForApi, uid, lobbyName, stageIdFromCache, storedUserData.themeName, user?.themeName, state?.correct, state?.total, state?.points, state?.time]);

  return (
    <div style={S.page}>
      <div style={S.inner}>
       

        <div style={S.card}>
          {isAnchorMode ? (
            <div
              style={{
                minHeight: 320,
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                gap: 10,
              }}
            >
              <div style={{ fontSize: 30, fontWeight: 900, color: "#2563eb" }}>
                🎉 You made it to the end! Now flood the chat with your reactions, memes, and questionable confidence 💬😂
              </div>
            
            </div>
          ) : (
            <>
              <div style={S.iconCircle}>
                <svg width="46" height="46" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M8 21h8" />
                  <path d="M12 17v4" />
                  <path d="M7 4h10" />
                  <path d="M17 4v3a5 5 0 0 1-10 0V4" />
                  <path d="M6 7H4a2 2 0 0 0 2 2" />
                  <path d="M18 7h2a2 2 0 0 1-2 2" />
                </svg>
              </div>

              <div style={S.title}>Game Over! 🎉</div>

              <div style={S.scoreBox}>
                <div style={S.score}>{score}</div>
                <div style={S.scoreLabel}>🏆 Total Points</div>
              </div>

              <div style={S.pill}>
                <span style={S.check}>✓</span>
                <span>
                  {correct} / {total} Correct Answers
                </span>
              </div>

              {time ? <div style={S.meta}>⏱️ Time: {time}</div> : null}

              <button
                type="button"
                style={S.btn}
                onClick={() => navigate("/feedback", { replace: true, state: { lobby_name: lobbyName } })}
              >
                Continue ➡️
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
